/**
 * runidea.ai — Phase 14 Wave 2 — Module C: Admin Deals.
 *
 * Cross-user deals browser. Each row supports: View, Reanalyze,
 * Override Verdict, Delete. Bulk-select supports bulk hard-delete.
 */
import { useCallback, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import AdminDataTable from "@/components/admin/AdminDataTable";
import TypedConfirmDialog from "@/components/admin/TypedConfirmDialog";
import VerdictBadge from "@/components/VerdictBadge";
import DealTypePill from "@/components/deal/DealTypePill";
import { Loader2, ShieldAlert } from "lucide-react";
import { useFmt } from "@/lib/useFmt";

const VERDICT_OPTIONS = ["buy", "monitor", "skip", "pass"];

function OverrideVerdictDialog({ open, onOpenChange, deal, onSaved }) {
  const [verdict, setVerdict] = useState(deal?.verdict || "monitor");
  const [reason, setReason] = useState("");
  const [saving, setSaving] = useState(false);
  // Sync internal state when a different row is opened.
  if (deal && verdict !== (deal._lastSeenId === deal.id ? verdict : null)) {
    // no-op; cheap guard
  }
  const submit = async () => {
    if (!verdict || !reason.trim()) {
      toast.error("Verdict + reason are required");
      return;
    }
    setSaving(true);
    try {
      await api.post(`/admin/deals/${deal.id}/override-verdict`, { verdict, reason: reason.trim() });
      toast.success(`Verdict overridden to "${verdict}"`);
      onSaved?.();
      onOpenChange(false);
      setReason("");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Override failed");
    } finally {
      setSaving(false);
    }
  };
  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) setReason(""); onOpenChange(v); }}>
      <DialogContent data-testid="admin-deal-override">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ShieldAlert className="h-4 w-4 text-amber-600" /> Override verdict
          </DialogTitle>
          <DialogDescription>
            Manually pin a verdict on this deal. The override is preserved in <code className="rounded bg-slate-100 px-1 text-xs">admin_override</code>; DealDetail surfaces &quot;Admin override: &lt;reason&gt;&quot;.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label className="text-xs text-slate-600">New verdict</Label>
            <div className="mt-1 flex flex-wrap gap-2">
              {VERDICT_OPTIONS.map((v) => (
                <button
                  key={v}
                  type="button"
                  onClick={() => setVerdict(v)}
                  data-testid={`override-verdict-${v}`}
                  className={`rounded-md border px-3 py-1.5 text-xs font-semibold uppercase tracking-wider transition-colors ${
                    verdict === v
                      ? "border-indigo-600 bg-indigo-50 text-indigo-700"
                      : "border-slate-200 text-slate-600 hover:bg-slate-50"
                  }`}
                >
                  {v}
                </button>
              ))}
            </div>
          </div>
          <div>
            <Label className="text-xs text-slate-600">Reason (visible to the user)</Label>
            <Textarea
              data-testid="override-verdict-reason"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              rows={3}
              placeholder="Why is the AI verdict being overridden?"
              className="mt-1 text-sm"
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>Cancel</Button>
          <Button
            onClick={submit}
            disabled={saving || !reason.trim()}
            data-testid="override-verdict-submit"
            className="bg-indigo-600 text-white hover:bg-indigo-700"
          >
            {saving ? <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" /> : null} Apply override
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function AdminDeals() {
  const fmt = useFmt();
  const [refresh, setRefresh] = useState(0);
  const [overriding, setOverriding] = useState(null);
  const [deleting, setDeleting] = useState(null);
  const [bulkPending, setBulkPending] = useState(null);
  const [reanalyzing, setReanalyzing] = useState(null);

  const fetchPage = useCallback(async ({ skip, limit, q }) => {
    const { data } = await api.get("/admin/deals", { params: { skip, limit, q } });
    return data;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refresh]);

  const reanalyze = async (deal) => {
    setReanalyzing(deal.id);
    try {
      const { data } = await api.post(`/admin/deals/${deal.id}/reanalyze`);
      toast.success(`Re-analyzed — verdict: ${data.verdict || "(unchanged)"}`);
      setRefresh((n) => n + 1);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Re-analyze failed");
    } finally {
      setReanalyzing(null);
    }
  };
  const doDelete = async (id) => {
    try {
      await api.delete(`/admin/deals/${id}`);
      toast.success("Deal deleted");
      setDeleting(null);
      setRefresh((n) => n + 1);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed");
    }
  };
  const doBulkDelete = async () => {
    if (!bulkPending?.ids?.length) return;
    try {
      const { data } = await api.post("/admin/deals/bulk-delete", { ids: bulkPending.ids });
      toast.success(`Deleted ${data.deleted} deals`);
      bulkPending.clear?.();
      setBulkPending(null);
      setRefresh((n) => n + 1);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Bulk delete failed");
    }
  };

  const columns = [
    { key: "property", label: "Property", render: (d) => (
      <div className="min-w-0">
        <div className="flex items-center gap-2">
          <span className="truncate font-medium text-slate-900">{d.property?.address || "—"}</span>
          <DealTypePill type={d.deal_type} size="sm" testid={`admin-deal-type-${d.id}`} />
        </div>
        <div className="font-mono text-[10px] text-slate-400">{d.id}</div>
      </div>
    )},
    { key: "verdict", label: "Verdict", render: (d) => (
      <div className="flex items-center gap-1.5">
        <VerdictBadge verdict={d.verdict} />
        {d.admin_override ? (
          <span data-testid={`admin-override-pill-${d.id}`} className="rounded-full bg-amber-100 px-1.5 py-0.5 text-[10px] font-semibold uppercase text-amber-700">override</span>
        ) : null}
      </div>
    )},
    { key: "cash_flow_monthly", label: "CF/mo", align: "right", render: (d) => (
      <span className={d.cash_flow_monthly >= 0 ? "text-emerald-700" : "text-red-600"}>
        {fmt.money(d.cash_flow_monthly, { cents: true })}
      </span>
    )},
    { key: "deal_score", label: "Score", align: "right", render: (d) => (
      <span className="font-semibold tabular-nums">{Number(d.deal_score || 0).toFixed(0)}</span>
    )},
    { key: "user_id", label: "Owner", render: (d) => (
      <span className="font-mono text-[10px] text-slate-500">{(d.user_id || "").slice(0, 8)}</span>
    )},
  ];

  return (
    <div className="space-y-6">
      <div>
        <p className="text-[10px] font-semibold uppercase tracking-[0.18em] text-indigo-600">Module C</p>
        <h1 className="mt-1 text-2xl font-bold tracking-tight text-slate-900">Deals</h1>
        <p className="mt-1 text-sm text-slate-500">Cross-user deal browser. View, reanalyze, override the AI verdict, or delete.</p>
      </div>

      <AdminDataTable
        testid="admin-deals"
        columns={columns}
        fetchPage={fetchPage}
        searchPlaceholder="Search by address (server-side q)…"
        emptyMessage="No deals."
        bulkActions={[
          { label: "Bulk delete", variant: "danger", onClick: (ids, clear) => setBulkPending({ ids, clear }) },
        ]}
        rowActions={(row) => [
          { label: "View", variant: "primary", onClick: (r) => window.open(`/deals/${r.id}`, "_blank") },
          { label: reanalyzing === row.id ? "…" : "Reanalyze", disabled: reanalyzing === row.id, onClick: reanalyze },
          { label: "Override", onClick: setOverriding },
          { label: "Delete", variant: "danger", onClick: setDeleting },
        ]}
      />

      <OverrideVerdictDialog
        open={!!overriding}
        onOpenChange={(v) => !v && setOverriding(null)}
        deal={overriding}
        onSaved={() => setRefresh((n) => n + 1)}
      />
      <TypedConfirmDialog
        open={!!deleting}
        onOpenChange={(v) => !v && setDeleting(null)}
        title="Delete this deal?"
        description={deleting ? `${deleting.property?.address} — this is a hard delete and will be audit-logged.` : ""}
        onConfirm={() => deleting && doDelete(deleting.id)}
        testid="admin-deal-delete"
      />
      <TypedConfirmDialog
        open={!!bulkPending}
        onOpenChange={(v) => !v && setBulkPending(null)}
        title={`Delete ${bulkPending?.ids?.length || 0} deals?`}
        description="Hard delete — each will be audit-logged separately."
        onConfirm={doBulkDelete}
        testid="admin-deal-bulkdelete"
      />
    </div>
  );
}
